import type {
  AppSettings,
  CancelPlanInput,
  CreatePlanInput,
  PlanStatus
} from "../types.js";
import { ApiFetchError } from "./api.js";

/**
 * Lógica pura dos botões da guia "Criar envios" (Criar / Editar / Cancelar /
 * Excluir): gating por status do plano, montagem dos payloads de
 * criação/cancelamento (a partir das Configurações) e detecção do 409 de
 * override. Sem React/DOM — testável em node. As ações reais (SP-API
 * createInboundPlan / cancelInboundPlan) rodam no backend.
 */

/**
 * EDITÁVEL: rascunho local cujo workflow ainda não começou (igual ao predicado
 * de Step1Produtos §6.1). Depois do createInboundPlan o plano fica somente
 * leitura — o botão vira "Visualizar".
 */
export function canEditPlan(plan: {
  status: PlanStatus;
  workflowStatus: string;
}): boolean {
  return plan.status === "DRAFT" && plan.workflowStatus === "NOT_STARTED";
}

// Status terminais que podem ser removidos da lista (já não há inbound ativo na
// Amazon): CANCELLED (já cancelado via cancelInboundPlan) e FAILED (workflow
// falhou). Espelha o guard do backend (DELETE /inbound-plans/:id).
const DELETABLE_TERMINAL_STATUSES: ReadonlySet<PlanStatus> = new Set<PlanStatus>([
  "CANCELLED",
  "FAILED"
]);

/**
 * EXCLUÍVEL (hard delete / remover da lista): rascunho NÃO iniciado, OU plano em
 * estado terminal — CANCELADO ou COM FALHA. Planos ATIVOS já registrados na
 * Amazon (REGISTERED_ON_AMAZON…BOX_LABELS_READY) NÃO podem ser excluídos: usam
 * Cancelar (cancelInboundPlan), senão virariam fantasma do lado da Amazon.
 */
export function canDeletePlan(plan: {
  status: PlanStatus;
  workflowStatus: string;
}): boolean {
  return (
    (plan.status === "DRAFT" && plan.workflowStatus === "NOT_STARTED") ||
    DELETABLE_TERMINAL_STATUSES.has(plan.status)
  );
}

// Estados terminais: cancelar não faz sentido (backend recusa CANCELLED com 409;
// FAILED já é terminal). Todos os demais podem ser cancelados (rascunho = local;
// registrado na Amazon = cancelInboundPlan via SP-API).
const NON_CANCELLABLE_STATUSES: ReadonlySet<PlanStatus> = new Set<PlanStatus>([
  "CANCELLED",
  "FAILED"
]);

export function canCancelPlan(plan: { status: PlanStatus }): boolean {
  return !NON_CANCELLABLE_STATUSES.has(plan.status);
}

/**
 * A origem do envio (Seller ID + os 6 campos do sourceAddress exigidos pela
 * SP-API) está completamente configurada em Configurações? O botão "Criar
 * envios" só cria o rascunho quando isto é true; senão manda o operador para
 * Configurações (não pede endereço no wizard).
 */
export function isSourceConfigComplete(settings: AppSettings): boolean {
  return (
    settings.amazonSellerId.trim().length > 0 &&
    settings.sourceAddressName.trim().length > 0 &&
    settings.sourceAddressLine1.trim().length > 0 &&
    settings.sourceAddressCity.trim().length > 0 &&
    settings.sourceAddressPostalCode.trim().length > 0 &&
    settings.sourceAddressCountryCode.trim().length > 0 &&
    settings.sourceAddressPhoneNumber.trim().length > 0
  );
}

/**
 * Monta o CreatePlanInput a partir das Configurações (sem form de endereço). O
 * `sourceAddress` contém EXATAMENTE os 6 campos exigidos (repassado à Amazon
 * como está). `name` (opcional) ajuda a identificar o plano na lista.
 */
export function buildCreatePlanInputFromSettings(
  settings: AppSettings,
  name?: string
): CreatePlanInput {
  const sourceAddress: Record<string, unknown> = {
    name: settings.sourceAddressName.trim(),
    addressLine1: settings.sourceAddressLine1.trim(),
    city: settings.sourceAddressCity.trim(),
    countryCode: settings.sourceAddressCountryCode.trim(),
    postalCode: settings.sourceAddressPostalCode.trim(),
    phoneNumber: settings.sourceAddressPhoneNumber.trim()
  };
  const trimmedName = name?.trim() ?? "";
  return {
    sellerId: settings.amazonSellerId.trim(),
    marketplaceId: settings.activeMarketplaceId,
    sourceAddress,
    destinationMarketplaces: [settings.activeMarketplaceId],
    ...(trimmedName.length > 0 ? { name: trimmedName } : {})
  };
}

/** Monta o CancelPlanInput. `adminOverride.approved` é sempre true quando enviado (§23). */
export function buildCancelInput(
  reason: string,
  overrideReason?: string
): CancelPlanInput {
  const trimmedOverride = overrideReason?.trim() ?? "";
  return {
    reason: reason.trim(),
    ...(trimmedOverride.length > 0
      ? { adminOverride: { approved: true, reason: trimmedOverride } }
      : {})
  };
}

/** Code do 409 quando a janela de void de 24h expirou e falta override Admin (§23). */
export const CANCEL_OVERRIDE_REQUIRED_CODE = "CANCEL_REQUIRES_ADMIN_OVERRIDE";

/** Detecta o 409 que exige confirmação do Admin para revelar o bloco de override. */
export function isCancelOverrideRequired(error: unknown): boolean {
  return (
    error instanceof ApiFetchError &&
    error.code === CANCEL_OVERRIDE_REQUIRED_CODE
  );
}
